package org.multiplatform.kotfolio.pages.compose

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.AppearanceAwareImage
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/compositional_locals")
@Composable
fun CompositionalLocalsComposePage() {

    BlogLayout(
        PageProperties(
            "Compositional Locals",
            "Unidirectional Data Flow, Why use CompositionLocal, staticCompositionLocalOf(), compositionLocalOf(), Creating Your Own CompositionLocals."
        ),
        BlogProperties("Jetpack Compose", "CompositionalLocals")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "The Hidden Engine of Jetpack Compose How does every Text() component in your app know what the primary color is without you passing it as a parameter? The answer is CompositionLocal. It's an incredibly powerful tool for implicit data passing, but performance depends on which flavor you choose: 1) compositionLocalOf 2) staticCompositionLocalOf",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
                    .textAlign(TextAlign.Start)
            )

            SpanText(
                text = "Composition Locals are like invisible messengers in Jetpack Compose. They allow any child composable — no matter how deeply nested — to read values provided higher up the composition tree. If you’ve used React Native, they’re similar to the Context API, which solves the “prop drilling” problem (passing props through components that don’t directly need them).",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
                    .textAlign(TextAlign.Start)
            )

            SpanText(
                text = "CompositionLocals shine when you’re dealing with cross-cutting UI stuff like theming or spacing. That’s where they save you from passing the same parameters through every composable.",
                modifier = Modifier.padding(bottom = 0.3.em)
                    .fontSize(FontSize.Medium)
                    .textAlign(TextAlign.Start)
            )

            SpanText(
                text = "CompositionLocal is an implicit approach to provide values in the composables.",
                modifier = Modifier.padding(bottom = 0.3.em)
                    .fontSize(FontSize.Medium)
                    .textAlign(TextAlign.Start)
            )

            SpanText(
                text = "CompositionLocal as a localized dependency injection system built directly into Compose’s composition model. You create a CompositionLocal instance, which acts as a key for a specific type of data. Then, using the CompositionLocalProvider, you define a scope within your UI tree and associate a value with that CompositionLocal key. Any composable within that scope can then access the provided value using the CompositionLocal.current property, without needing to know where the value originated. This not only simplifies code but also makes it more maintainable and reusable.",
                modifier = Modifier.padding(bottom = 0.3.em)
                    .fontSize(FontSize.Medium)
                    .textAlign(TextAlign.Start)
            )

            SpanText(
                text = "Compositional Locals is to provide an object instance that is accessible to all its children. For instance, your LocalContext.current serves as an example of a compositional local. This allows you to easily access the context in any composable, which is precisely what we require. We need something like LocalNavController.current. With this capability at our disposal, we no longer have to pass an instance of NavController to every composable method.",
                modifier = Modifier.padding(bottom = 0.3.em)
                    .fontSize(FontSize.Medium)
                    .textAlign(TextAlign.Start)
            )

            SpanText(
                text = "Used to provide contextual data without expanding the scope of influence. CompositionLocal provides a mechanism for passing data down through the composition implicitly, without needing to pass it through every composable function. This can be particularly useful when the data is frequently used across many parts of the UI, such as theme-related information (colors, typography, etc.)",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
                    .textAlign(TextAlign.Start)
            )

            SpanText(
                text = "Composition Local is a powerful tool in Jetpack Compose that allows you to pass data down the composable tree without explicitly passing it through each composable. This feature is handy for managing, sharing data within a composable hierarchy without the need to explicitly pass data through each level of the UI tree, global configurations, themes and settings that must be accessed throughout your application.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
                    .textAlign(TextAlign.Start)
            )

            SpanText(
                text = "CompositionLocal starts with the creation of a ProvidableCompositionLocal instance which can be obtained via a call to either the compositionLocalOf() or staticCompositionLocalOf() function. In each case the function accepts a lambda defining a default value to be assigned to the state in the absence of a specific assignment,",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
                    .textAlign(TextAlign.Start)
            )

            SpanText(
                text = "A problem in normal composable function tree",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Before we know what CompositionLocal is, let’s understand what problem the composable function tree has.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Imagine there’s a state variable data that will affect components deep beneath the top-level composable function, i.e. affected components that are indicated by the green color boxes below, then we need to send the state variable data through a lot of composable functions (as indicated by the blue color boxes below).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This makes development in Jetpack Compose tedious with long parameters for each composable functions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Of course, we can make a static global object accessible to all composable functions, but that’s a pretty bad programming practice.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }
            }

            SpanText(
                text = "Benefits of CompositionLocal",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Implicit Sharing: CompositionLocal enables data to be shared across composables without explicitly passing it as a parameter. This allows easier management of globally relevant data, such as theme styles, user settings, or app-wide configurations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Localized Context: CompositionLocal allows for different parts of the UI to have their own context, which can vary depending on the scope in which the CompositionLocal is provided. This is useful for customizing different sections of the UI while maintaining overall consistency",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Reactivity: For dynamic values, CompositionLocal can trigger recompositions when its value changes. This means that when a value is updated, only the parts of the UI that depend on this value will be recomposed, keeping the UI efficient and responsive.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Decoupled Components: CompositionLocal decouples UI components from their direct dependencies. This enhances the modularity and reusability of composables, as they don’t need to be aware of the specific data provided higher in the composition tree. This makes testing and reusing components easier.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }
            }

            SpanText(
                text = "CompositionLocal to provides an implicit way of data-flow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We can use CompositionLocal to help implicitly pass the state variable data without the need to pass it through each of the composite functions within the chain.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Each of the composable functions below the tree can now easily access the state variable data without the need to be passed in.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }
            }

            SpanText(
                text = "How is this better than the static global object?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If we look at the diagram above, it looks no different than a static global object. However, the CompositeLocal provided is only available for the tree underneath it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For better illustration, let’s assume we only provide the CompositionLocal with the state variable data on the left side of the tree as shown below, now the right side of the tree will no longer get the state variable data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }
            }

            SpanText(
                text = "Local Provider",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "Creates a CompositionLocal key that can be provided using CompositionLocalProvider. Changing the value will invalidate the children of CompositionLocalProvider that read the value using CompositionLocal.current. This is where you define the value you want to propagate. You use CompositionLocalProvider to specify the value at a particular level in the composable tree.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
                    .textAlign(TextAlign.Start)
            )

            SpanText(
                text = "Providing Values to a CompositionLocal",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "When the provided value updates, any composable consuming it will automatically recompose.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
                    .textAlign(TextAlign.Start)
            )

            SpanText(
                text = "The CompositionLocalProvider composable binds values to CompositionLocal instances within a specific hierarchy. To provide a new value to a CompositionLocal, the provides infix function is used, which associates a CompositionLocal key with a value. Here’s how you can provide values:",
                modifier = Modifier.padding(bottom = 0.3.em)
                    .fontSize(FontSize.Medium)
                    .textAlign(TextAlign.Start)
            )

            SpanText(
                text = "In this example, CompositionLocalProvider ensures that LocalColors is provided a new set of colors, which will be accessible to all descendant composables within that composition block.",
                modifier = Modifier.padding(bottom = 0.3.em)
                    .fontSize(FontSize.Medium)
                    .textAlign(TextAlign.Start)
            )

            //https://github.com/aakash-mangukiya/KMM-Movie-App.git
            KotlinCode(
                code = """
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.CompositionLocalProvider
                    
                    @Composable
                    fun MovieAppTheme(
                        content: @Composable () -> Unit
                    ) {
                        CompositionLocalProvider(
                            LocalRadiusProvider provides Radius(),
                            LocalTypographyProvider provides MovieTypography(),
                            LocalSpacingProvider provides Spacing(),
                            LocalColorProvider provides ColorPalette()
                        ) {
                            content()
                        }
                    }
                    
                    val LocalRadiusProvider = staticCompositionLocalOf { Radius() }
                    val LocalTypographyProvider = staticCompositionLocalOf { MovieTypography() }
                    val LocalSpacingProvider = staticCompositionLocalOf { Spacing() }
                    val LocalColorProvider = staticCompositionLocalOf { ColorPalette() }
                    
                    data class Radius(
                        val none: Dp = 0.dp,
                        val small: Dp = 4.dp,
                        val medium: Dp = 6.dp,
                        val large: Dp = 8.dp,
                        val xl: Dp = 10.dp,
                        val x2l: Dp = 12.dp,
                        val x3l: Dp = 14.dp,
                        val x4l: Dp = 16.dp
                    )
                    
                    @Suppress("PropertyName")
                    data class Spacing(
                        val spacing_0: Dp = 0.dp,
                        val spacing_0_5: Dp = 2.dp,
                        val spacing_1: Dp = 4.dp,
                        val spacing_2: Dp = 8.dp,
                        val spacing_3: Dp = 12.dp,
                        val spacing_4: Dp = 16.dp,
                        val spacing_5: Dp = 20.dp,
                        val spacing_6: Dp = 24.dp,
                    )
                    
                    //Access
                    val spacingProvider = LocalSpacingProvider.current
                    val typographyProvider = LocalTypographyProvider.current
                    val colorProvider = LocalColorProvider.current
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Color
                    @Composable
                    fun App() {
                        val customThemeColors = Colors( /* custom theme colors */ )

                        // Providing new values to the CompositionLocal
                        CompositionLocalProvider(LocalColors provides customThemeColors) {
                            // The provided value is now accessible in this part of the Composition
                            MyAppContent()
                        }
                    }
                    
                    //2 Image 
                    @Composable
                    internal fun AppTheme(
                        content: @Composable () -> Unit
                    ) {
                        
                        val scheme = rememberDynamicColorScheme(
                            seedColor = Color(0xFF5FBA9A),
                            isDark = true,
                            isAmoled = false,
                            style = PaletteStyle.Monochrome
                        )
                        MaterialTheme(
                            colorScheme = scheme,
                            content = { Surface(content = content) }
                        )
                    }

                    @Composable
                    internal fun App(
                        navController: NavHostController = rememberNavController(),
                    ) = AppTheme {
                        val context = LocalPlatformContext.current
                        val imageLoader = remember(context) { ImageLoader(context) }

                        CompositionLocalProvider(
                            LocalImageLoader provides imageLoader,

                            ) {
                        }
                    }
                    
                    AsyncImage(
                        contentDescription = null,
                        contentScale = ContentScale.Crop,
                        clipToBounds = true,
                        imageLoader = LocalImageLoader.current
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "Local Consumer",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "This is where you access the propagated value. You use current property of a CompositionLocal to get the value at any point in the composable hierarchy.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
                    .textAlign(TextAlign.Start)
            )

            SpanText(
                text = ".current",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "The .current property is synchronized with Compose's composition system. You can’t accidentally access CompositionLocals from background threads because they’re only available in @Composable functions. The .current property is how you actually read a value from a CompositionLocal. The .current property fetches the nearest value up the composition tree. Be mindful of scoping, as consuming a value from an unexpected level might lead to inconsistencies. Always ensure that the provider hierarchy is intentional and reflects the desired scope.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
                    .textAlign(TextAlign.Start)
            )

            SpanText(
                text = "When to Use CompositionLocal",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You can provide a value through CompositionLocal when the value is a UI tree-wide value. As you saw before with navController, the theme-related values and paddings you implemented in the previous sections can be used by all composables, a subset, and even several composables at once.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This method seems fine on the surface, but it is not very convenient in some scenarios. For example, if function A nests function B, function B nests function C, and all functions A, B, and C use this parameter, then all three functions need to define this parameter, which is not very convenient.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You can provide a value through CompositionLocal when the value is a UI tree-wide value. As you saw before with navController, the theme-related values and paddings you implemented in the previous sections can be used by all composables, a subset, and even several composables at once.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }
            }

            SpanText(
                text = "When to avoid CompositionLocal",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For data Specific to a Single Composable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Frequently Changing Data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }
            }

            SpanText(
                text = "Creating Your Own CompositionLocals",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In some scenarios, you may want to implement your own CompositionLocal. For example, to provide the navigation controller to the different composables in your UI or implement a custom theme for your app.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose provides two ways to use CompositionLocal, depending on the frequency that the data changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }
            }

            SpanText(
                text = "Static Composition Local Provider (staticCompositionLocalOf)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose does not track the places where staticCompositionLocalOf is read. When the value changes, the entire content block where the CompositionLocal is provided gets recomposed, instead of just the places where the current value is read in the Composition. It is best used for data that rarely changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Static Composition Locals are used for values that do not change frequently or are meant to remain constant throughout the lifecycle of the app. (like design tokens—typography, spacing, etc.)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When using this, any change on the CompositionLocal value will cause the entire UI to redraw.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When the value of your CompositionLocal doesn’t change often, staticCompositionLocalOf() is a good choice. A good place to use it is with the navController in the app.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "They do not trigger recompositions when their values are updated, making them ideal for providing configurations or settings that are unlikely to change.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Global settings like debug configurations.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Constants such as API endpoints, static themes, or feature flags.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Any value that should remain stable and not reactively update the UI.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }
                }
            }

            //https://github.com/AnatoliiChub/HRAM.git
            KotlinCode(
                code = """
                     val customTextSelectionColors = TextSelectionColors(
                        handleColor = DarkRedShadow,
                        backgroundColor = DarkRedShadow.copy(alpha = 0.4f)
                    )
                    
                    CompositionLocalProvider(LocalTextSelectionColors provides customTextSelectionColors) {
                        TextField()
                    }    
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Drawer
                    
                    val LocalDrawerController = staticCompositionLocalOf<DrawerController> {
                        error("No DrawerController provided")
                    }

                    class DrawerController(
                        private val drawerState: DrawerState,
                        private val scope: CoroutineScope
                    ) {
                        fun open() = scope.launch { drawerState.open() }
                        fun close() = scope.launch { drawerState.close() }
                    }
                    
                    fun App() {
                        val drawerState = rememberDrawerState(DrawerValue.Closed)
                        val scope = rememberCoroutineScope()
                        val drawerController = remember { DrawerController(drawerState, scope) }
                        CompositionLocalProvider(LocalDrawerController provides drawerController) {
                            ModalNavigationDrawer(
                                drawerState = drawerState,
                                drawerContent = {
                                    
                                },
                                content = {
                                   
                                },
                            )
                        }
                    }
                    
                    //Access
                    val drawerController = LocalDrawerController.current
                    drawerController.open()
                    """.trimIndent()
            )

            //https://github.com/Rome-Squad/CineVerse-App
            KotlinCode(
                code = """
                    //Social Icon
                    
                    data class CineVerseIcons(
                        //val outline: Outline = outlineIcons,
                        //val bold: Bold = boldIcons,
                        //val dueTone: DueTone = dueToneIcons,
                        val colored: Colored = coloredIcons,
                    )

                    internal val LocalCineVerseIcons = staticCompositionLocalOf { CineVerseIcons() }
                    
                    object Theme {
                        val icons: CineVerseIcons
                            @Composable @ReadOnlyComposable get() = LocalCineVerseIcons.current
                    }
                   
                    @Composable
                    fun CineVerseTheme(
                        isDarkTheme: Boolean = isSystemInDarkTheme(),
                        content: @Composable () -> Unit
                    ) {
                        val theme = if (isDarkTheme) darkThemeColor else lightThemeColor
                        CompositionLocalProvider(
                            LocalCineVerseColors provides theme,
                            LocalCineVerseTextStyle provides defaultTextStyle,
                            LocalCineVerseRadius provides CineVerseRadius(),
                            LocalCineVerseIcons provides CineVerseIcons(),
                        ) {
                            content()
                        }
                    }
                    
                    data class Colored(
                        val logo: Int,
                        val confusedFace: Int,
                        val facebook: Int,
                        val frowningFace: Int,
                        val instagram: Int,
                        val iraqFlag: Int,
                        val neutralFace: Int,
                        val smilingFaceWithSmilingEyes: Int,
                        val starStruck: Int,
                        val tiktok: Int,
                        val ukFlag: Int,
                        val x: Int,
                        val youtube: Int,
                    )

                    val coloredIcons = Colored(
                        logo = R.drawable.colored_cineverse_logo,
                        confusedFace = R.drawable.colored_confused_face,
                        facebook = R.drawable.colored_facebook,
                        frowningFace = R.drawable.colored_frowning_face,
                        instagram = R.drawable.colored_instagram,
                        iraqFlag = R.drawable.colored_iraq_flag,
                        neutralFace = R.drawable.colored_neutral_face,
                        smilingFaceWithSmilingEyes = R.drawable.colored_smiling_face_with_smiling_eyes,
                        starStruck = R.drawable.colored_star_struck,
                        tiktok = R.drawable.colored_tiktok,
                        ukFlag = R.drawable.colored_uk_flag,
                        x = R.drawable.colored_x,
                        youtube = R.drawable.colored_youtube
                    )
                    
                    data class PersonSocialMediaLinks(
                        val facebookLink: String? = null,
                        val instagramLink: String? = null,
                        val youtubeLink: String? = null,
                        val tiktokLink: String? = null,
                        val twitterLink: String? = null,
                    )
                    
                    data class SocialMediaUi(
                        val platform: SocialMediaPlatform,
                        val url: String,
                        val name: Int,
                        val contentDescription: Int
                    )

                    enum class SocialMediaPlatform {
                        YOUTUBE, FACEBOOK, INSTAGRAM, TWITTER, TIKTOK
                    }

                    @Composable
                    fun SocialMediaPlatform.getIcon(): Painter {
                        return when (this) {
                            SocialMediaPlatform.YOUTUBE -> painterResource(Theme.icons.colored.youtube)
                            SocialMediaPlatform.FACEBOOK -> painterResource(Theme.icons.colored.facebook)
                            SocialMediaPlatform.INSTAGRAM -> painterResource(Theme.icons.colored.instagram)
                            SocialMediaPlatform.TWITTER -> painterResource(Theme.icons.colored.x)
                            SocialMediaPlatform.TIKTOK -> painterResource(Theme.icons.colored.tiktok)
                        }
                    }

                    fun PersonSocialMediaLinks.toUiState(): List<SocialMediaUi> {
                        val socialMediaUiList: MutableList<SocialMediaUi> = mutableListOf()
                        youtubeLink?.let { url ->
                            socialMediaUiList.add(
                                SocialMediaUi(
                                    platform = SocialMediaPlatform.YOUTUBE,
                                    url = url,
                                    name = R.string.youtube,
                                    contentDescription = R.string.youtube_icon
                                )
                            )
                        }

                        facebookLink?.let { url ->
                            socialMediaUiList.add(
                                SocialMediaUi(
                                    platform = SocialMediaPlatform.FACEBOOK,
                                    url = url,
                                    name = R.string.facebook,
                                    contentDescription = R.string.facebook_icon
                                )
                            )
                        }

                        instagramLink?.let { url ->
                            socialMediaUiList.add(
                                SocialMediaUi(
                                    platform = SocialMediaPlatform.INSTAGRAM,
                                    url = url,
                                    name = R.string.instagram,
                                    contentDescription = R.string.instagram_icon
                                )
                            )
                        }

                        twitterLink?.let { url ->
                            socialMediaUiList.add(
                                SocialMediaUi(
                                    platform = SocialMediaPlatform.TWITTER,
                                    url = url,
                                    name = R.string.twitter,
                                    contentDescription = R.string.twitter_icon
                                )
                            )
                        }

                        tiktokLink?.let { url ->
                            socialMediaUiList.add(
                                SocialMediaUi(
                                    platform = SocialMediaPlatform.TIKTOK,
                                    url = url,
                                    name = R.string.tiktok,
                                    contentDescription = R.string.tiktok_icon
                                )
                            )
                        }
                        return socialMediaUiList
                    }
                    
                    data class Person(
                        val socialMedia: PersonSocialMediaLinks? = null
                    )
                    
                    fun mapToPerson(
                        socialMedia: PersonSocialMediaDto
                    ): Person = Person(
                        socialMedia = socialMedia.toEntity()
                    )
                    
                    private fun PersonSocialMediaDto.toEntity(): PersonSocialMediaLinks = PersonSocialMediaLinks(
                        facebookLink = facebookId.prependIfNotBlank(FACEBOOK_URL),
                        instagramLink = instagramId.prependIfNotBlank(INSTAGRAM_URL),
                        youtubeLink = youtubeId.prependIfNotBlank(YOUTUBE_URL),
                        tiktokLink = tiktokId.prependIfNotBlank(TIKTOK_URL + AT_SYMBOLS_URL),
                        twitterLink = twitterId.prependIfNotBlank(X_URL),
                    )
                    
                    const val BASE_IMAGE_URL = "https://image.tmdb.org/t/p/w500"
                    const val INSTAGRAM_URL = "https://www.instagram.com/"
                    const val FACEBOOK_URL = "https://facebook.com/"
                    const val YOUTUBE_URL = "https://www.youtube.com/"
                    const val X_URL = "https://x.com/"
                    const val TIKTOK_URL = "https://www.tiktok.com/"
                    const val AT_SYMBOLS_URL = "@"
                    
                    //get data
                    person.socialMedia?.toUiState()
                    
                    data class CastDetailsUiState(
                        val socialMediaUiList: List<SocialMediaUi> = emptyList()
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Global State Management with User Context
                    
                    // Define a UserSession data class
                    data class UserSession(val userId: String, val authToken: String)

                    // Define a Composition Local for UserSession
                    val LocalUserSession = compositionLocalOf<UserSession?> { null }
                    
                    @Composable
                    fun App() {
                        val session = remember { mutableStateOf<UserSession?>(null) }
                        CompositionLocalProvider(LocalUserSession provides session.value) {
                            if (session.value != null) {
                                HomeScreen()
                            } else {
                                LoginScreen(onLogin = { user -> session.value = user })
                            }
                        }
                    }
                    @Composable
                    fun LoginScreen(onLogin: (UserSession) -> Unit) {
                        Button(onClick = { onLogin(UserSession("user123", "token123")) }) {
                            Text("Log In")
                        }
                    }
                    @Composable
                    fun HomeScreen() {
                        val session = LocalUserSession.current
                        Text(session?.userId)
                    }
                   """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "CompositionLocal allows you to define and provide values in a composable hierarchy, making it a perfect candidate for managing NavController instances. Instead of passing the controller through every composable, you can set it once and use it anywhere in your composable tree.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Provide NavController: Use CompositionLocalProvider to provide the NavController in a top-level composable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use the Provided NavController: Access the NavController in your screens using LocalNavController.current.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }
            }

            //https://github.com/NehaKushwah993/CompositionLocal-Navigation
            KotlinCode(
                code = """
                    //val LocalNavController = staticCompositionLocalOf<NavHostController?> { null }
                    
                    val LocalNavigationProvider = staticCompositionLocalOf { error("No navigation host controller provided.") }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val LocalNavController = staticCompositionLocalOf<NavHostController?> { null }
                    
                    @Composable
                    fun MainScreen() {
                        val bottomNavController = rememberNavController()

                        Scaffold(
                            bottomBar = {
                                BottomNavigationBar(bottomNavController = bottomNavController)
                            }
                        ) { innerPadding ->
                            CompositionLocalProvider(LocalNavController provides bottomNavController) {
                                NavHost(
                                    navController = bottomNavController,
                                    startDestination = "home",
                                    Modifier.padding(innerPadding)
                                ) {
                                    
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Dynamic Composition Local Provider (compositionLocalOf)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dynamic Composition Locals are reactive and will trigger recompositions when their values change. They are ideal for managing stateful data that needs to be updated frequently and observed by multiple composables.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Contrary to staticCompositionLocalOf, compositionLocalOf will only invalidate the composables that read its current value. To make use of compositionLocalOf, you need to provide values for a couple of paddings used in the book lists.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "dynamic UI themes that can change based on user preference, localization settings, or user-specific configurations.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Navigation state management using NavController.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Context-sensitive data like localization settings or user-specific preferences.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    val LocalNavController = compositionLocalOf<NavController> { error("No NavController found!") }
                    
                    override fun onCreate(savedInstanceState: Bundle?) {
                        super.onCreate(savedInstanceState)
                        setContent {
                            YourAppTheme {
                                val navHost = rememberNavController()
                      
                                CompositionLocalProvider(LocalNavController provides navHost) {
                                    RootNavigationComposable()
                                }
                            }
                        }
                    }
                    
                    NavHost(navController = host, startDestination = Destination.Home.route) {
                      composable(Destination.Home.route) {
                        Home(
                          goToProfileMenu = { host.navigate(Destination.ProfileMenu.route) }
                        )    
                      }

                      composable(Destination.ProfileMenu.route) {
                        ProfileMenu()
                      }
                    }
                    
                    val navController = LocalNavController.current
                    Button(onClick = { navController.navigate("someScreen") })
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    data class SizeDimensions(
                        val default: Dp = 4.dp,
                        val extraExtraExtraSmall: Dp = 8.dp,
                        val extraExtraSmall: Dp = 12.dp,
                        val extraSmall: Dp = 16.dp,
                        val small: Dp = 20.dp,
                        val medium: Dp = 24.dp,
                        val large: Dp = 28.dp,
                        val extraLarge: Dp = 32.dp,
                        val extraExtraLarge: Dp = 36.dp,
                        val extraExtraExtraLarge: Dp = 40.dp,
                        val huge: Dp = 44.dp,
                        val gigantic: Dp = 48.dp,
                        val extraExtraHuge: Dp = 52.dp,
                        val extraExtraExtraHuge: Dp = 56.dp,
                        val extraExtraExtraExtraHuge: Dp = 60.dp,
                    )

                    val LocalSize = compositionLocalOf { SizeDimensions() }
                    
                    @Composable
                    fun CoachTheme(
                        darkTheme: Boolean = isSystemInDarkTheme(),
                        // Dynamic color is available on Android 12+
                        dynamicColor: Boolean = true,
                        content: @Composable () -> Unit
                    ) {
                        val colorScheme = when {
                    //        dynamicColor && Build.VERSION.SDK_INT >= Build.VERSION_CODES.S -> {
                    //            val context = LocalContext.current
                    //            if (darkTheme) dynamicDarkColorScheme(context) else dynamicLightColorScheme(context)
                    //        }

                            darkTheme -> DarkColorScheme
                            else -> LightColorScheme
                        }
                        CompositionLocalProvider(
                            LocalSpacing provides SpaceDimensions(),
                            LocalFontSize provides FontDimensions(),
                            LocalSize provides SizeDimensions(),
                        ) {
                            MaterialTheme(
                                colorScheme = colorScheme,
                                typography = Typography,
                                content = content
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    data class MyReadingPaddings(
                      val small: Dp,
                      val medium: Dp
                    )

                    val LocalPaddings = compositionLocalOf { MyReadingPaddings(small = 8.dp, medium = 16.dp) }

                    padding(all = LocalPaddings.current.small)
                    padding(LocalPaddings.current.medium)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    CompositionLocalProvider(
                        LocalTextToolbar provides SungbinLandTextToolbar(LocalView.current),
                        content = content
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val LocalAnalytics = staticCompositionLocalOf<Analytics> {
                        LoggingAnalytics()
                    }
                    
                    val analytics = FirebaseAnalytics()
                    setContent {
                        AppTheme {
                            CompositionLocalProvider(LocalAnalytics provides analytics) {
                                HomeScreen()
                            }
                        }
                    }

                    @Composable
                    fun HomeScreen(viewModel : MyViewModel = hiltViewModel()) {
                        val state =
                        val analytics = LocalAnalytics.current
                        Button(onClick = { analytics.logEvent("buttonClick") }) {
                            Text("Click me!")
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/xephosbot/AnilibriaRefresh
            KotlinCode(
                code = """
                    //shimmer CompositionLocalProvider
                    
                    shimmer = "1.3.3"
                    shimmer-compose = { module = "com.valentinilk.shimmer:compose-shimmer", version.ref = "shimmer" }
                    
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.CompositionLocalProvider
                    import androidx.compose.runtime.compositionLocalOf
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.layout.onGloballyPositioned
                    import com.valentinilk.shimmer.Shimmer
                    import com.valentinilk.shimmer.shimmer
                    import com.valentinilk.shimmer.unclippedBoundsInWindow

                    @Composable
                    fun Modifier.shimmerSafe(shimmer: Shimmer?) = then(
                        if (shimmer != null) {
                            Modifier.shimmer(shimmer)
                        } else {
                            Modifier
                        },
                    )

                    fun Modifier.shimmerUpdater(shimmer: Shimmer) = onGloballyPositioned {
                        val position = it.unclippedBoundsInWindow()
                        shimmer.updateBounds(position)
                    }

                    @Composable
                    fun ProvideShimmer(value: Shimmer, content: @Composable () -> Unit) {
                        CompositionLocalProvider(LocalShimmer provides value, content = content)
                    }

                    val LocalShimmer = compositionLocalOf<Shimmer?> { null }
                    
                    //1. LazyColumn List
                    //val shimmer = rememberShimmer(ShimmerBounds.Window)
                    val shimmer = rememberShimmer(ShimmerBounds.Custom)
                    
                    ProvideShimmer(shimmer) {
                        //modifier.shimmerUpdater(shimmer),
                    }
                    
                    //2
                    val shimmer = LocalShimmer.current
                    ProvideShimmer(shimmer) {
                        //modifier.shimmerSafe(shimmer)
                    }
                    
                    //3
                    val shimmer = rememberShimmer(ShimmerBounds.Custom)
                    ProvideShimmer(shimmer) {
                        //modifier.shimmerUpdater(shimmer)
                    }
                    
                    //4. Image Loading
                    @Composable
                    fun PosterImage(
                        poster: Poster?,
                        modifier: Modifier = Modifier,
                        contentDescription: String? = null,
                        contentScale: ContentScale = ContentScale.Crop,
                        placeholder: Painter? = null,
                    ) {
                        val shimmer = LocalShimmer.current
                        var isLoading by rememberSaveable(poster) { mutableStateOf(true) }

                        Box(
                            modifier = modifier,
                            contentAlignment = Alignment.Center,
                        ) {
                            AsyncImage(
                                modifier = Modifier.fillMaxSize(),
                                model = poster,
                                placeholder = rememberAsyncImagePainter(poster?.thumbnail),
                                error = placeholder ?: painterResource(Res.drawable.placeholder_default),
                                contentDescription = contentDescription,
                                contentScale = contentScale,
                                onLoading = {
                                    isLoading = true
                                },
                                onSuccess = {
                                    isLoading = false
                                },
                                onError = {
                                    isLoading = false
                                }
                            )
                            AnimatedVisibility(
                                visible = isLoading,
                                enter = fadeIn(),
                                exit = fadeOut(),
                            ) {
                                Box(
                                    modifier = Modifier
                                        .fillMaxSize()
                                        .shimmerSafe(shimmer)
                                        .background(Color.LightGray),
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/xephosbot/AnilibriaRefresh
            KotlinCode(
                code = """
                    @OptIn(ExperimentalMaterial3ExpressiveApi::class)
                    @Composable
                    fun Header(
                        title: @Composable () -> Unit,
                        modifier: Modifier = Modifier,
                        onClick: (() -> Unit)? = null,
                    ) {
                        Box(Modifier.padding(top = 8.dp)) {
                            Row(
                                modifier = modifier
                                    .fillMaxWidth()
                                    .height(52.dp)
                                    .padding(horizontal = 16.dp),
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                //Material 3 default provide
                                ProvideTextStyle(MaterialTheme.typography.titleMedium) {
                                    title()
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "CompositionLocal allows you to define and provide values in a composable hierarchy, making it a perfect candidate for managing NavController instances. Instead of passing the controller through every composable, you can set it once and use it anywhere in your composable tree.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
                    .textAlign(TextAlign.Start)
            )

            //https://github.com/tientuyen07/CommonForKMP
            //https://github.com/haidrrrry/Authenticator
            KotlinCode(
                code = """
                    import androidx.compose.ui.graphics.Color

                    data class AppTheme(
                        val primary: Color,
                        val primaryVariant: Color,
                        val primaryLight: Color,
                        val background: Color,
                        val backgroundSecondary: Color,
                        val surface: Color,
                        val surfaceVariant: Color,
                        val onSurface: Color,
                        val onSurfaceVariant: Color,
                        val onPrimary: Color,
                        val cardBackground: Color,
                        val cardElevated: Color,
                        val success: Color,
                        val warning: Color,
                        val error: Color,
                        val timerGood: Color,
                        val timerWarning: Color,
                        val timerCritical: Color,
                        val isLight: Boolean
                    )
                    
                    object AppColors {
                        // Light Theme Colors
                        object Light {
                            val Primary = Color(0xFF6366F1) // Indigo-500
                            val PrimaryVariant = Color(0xFF4F46E5) // Indigo-600
                            val PrimaryLight = Color(0xFF8B5CF6) // Violet-500

                            val Background = Color(0xFFF8FAFC) // Slate-50
                            val BackgroundSecondary = Color(0xFFF1F5F9) // Slate-100
                            val Surface = Color(0xFFFFFFFF) // White
                            val SurfaceVariant = Color(0xFFF8FAFC) // Slate-50

                            val OnSurface = Color(0xFF0F172A) // Slate-900
                            val OnSurfaceVariant = Color(0xFF64748B) // Slate-500
                            val OnPrimary = Color(0xFFFFFFFF) // White

                            val CardBackground = Color(0xFFFFFFFF)
                            val CardElevated = Color(0xFFF8FAFC)

                            val Success = Color(0xFF10B981) // Emerald-500
                            val Warning = Color(0xFFF59E0B) // Amber-500
                            val Error = Color(0xFFEF4444) // Red-500

                            val TimerGood = Color(0xFF059669) // Emerald-600
                            val TimerWarning = Color(0xFFD97706) // Amber-600
                            val TimerCritical = Color(0xFFDC2626) // Red-600
                        }

                        // Dark Theme Colors
                        object Dark {
                            val Primary = Color(0xFF818CF8) // Indigo-400 (lighter for dark theme)
                            val PrimaryVariant = Color(0xFF6366F1) // Indigo-500
                            val PrimaryLight = Color(0xFFA78BFA) // Violet-400

                            val Background = Color(0xFF0F172A) // Slate-900
                            val BackgroundSecondary = Color(0xFF1E293B) // Slate-800
                            val Surface = Color(0xFF1E293B) // Slate-800
                            val SurfaceVariant = Color(0xFF334155) // Slate-700

                            val OnSurface = Color(0xFFF8FAFC) // Slate-50
                            val OnSurfaceVariant = Color(0xFF94A3B8) // Slate-400
                            val OnPrimary = Color(0xFF1E293B) // Slate-800

                            val CardBackground = Color(0xFF1E293B) // Slate-800
                            val CardElevated = Color(0xFF334155) // Slate-700

                            val Success = Color(0xFF34D399) // Emerald-400
                            val Warning = Color(0xFFFBBF24) // Amber-400
                            val Error = Color(0xFFF87171) // Red-400

                            val TimerGood = Color(0xFF10B981) // Emerald-500
                            val TimerWarning = Color(0xFFF59E0B) // Amber-500
                            val TimerCritical = Color(0xFFEF4444) // Red-500
                        }
                    }
                    
                    class ThemeManager {
                        private val _themeMode = MutableStateFlow(ThemeMode.SYSTEM)
                        val themeMode: StateFlow<ThemeMode> = _themeMode.asStateFlow()

                        fun setThemeMode(mode: ThemeMode) {
                            _themeMode.value = mode
                            // Here you would persist the theme preference
                            // For now, we'll use in-memory storage as per artifact restrictions
                        }

                        fun getCurrentThemeMode(): ThemeMode = _themeMode.value
                    }
                    
                    @Composable
                    fun ProvideAppTheme(
                        themeManager: ThemeManager,
                        content: @Composable () -> Unit
                    ) {
                        val themeMode by themeManager.themeMode.collectAsState()
                        val isSystemDark = isSystemInDarkTheme()

                        val isDarkTheme = when (themeMode) {
                            ThemeMode.LIGHT -> false
                            ThemeMode.DARK -> true
                            ThemeMode.SYSTEM -> isSystemDark
                        }

                        val theme = if (isDarkTheme) {
                            AppTheme(
                                primary = AppColors.Dark.Primary,
                                primaryVariant = AppColors.Dark.PrimaryVariant,
                                primaryLight = AppColors.Dark.PrimaryLight,
                                background = AppColors.Dark.Background,
                                backgroundSecondary = AppColors.Dark.BackgroundSecondary,
                                surface = AppColors.Dark.Surface,
                                surfaceVariant = AppColors.Dark.SurfaceVariant,
                                onSurface = AppColors.Dark.OnSurface,
                                onSurfaceVariant = AppColors.Dark.OnSurfaceVariant,
                                onPrimary = AppColors.Dark.OnPrimary,
                                cardBackground = AppColors.Dark.CardBackground,
                                cardElevated = AppColors.Dark.CardElevated,
                                success = AppColors.Dark.Success,
                                warning = AppColors.Dark.Warning,
                                error = AppColors.Dark.Error,
                                timerGood = AppColors.Dark.TimerGood,
                                timerWarning = AppColors.Dark.TimerWarning,
                                timerCritical = AppColors.Dark.TimerCritical,
                                isLight = false
                            )
                        } else {
                            AppTheme(
                                primary = AppColors.Light.Primary,
                                primaryVariant = AppColors.Light.PrimaryVariant,
                                primaryLight = AppColors.Light.PrimaryLight,
                                background = AppColors.Light.Background,
                                backgroundSecondary = AppColors.Light.BackgroundSecondary,
                                surface = AppColors.Light.Surface,
                                surfaceVariant = AppColors.Light.SurfaceVariant,
                                onSurface = AppColors.Light.OnSurface,
                                onSurfaceVariant = AppColors.Light.OnSurfaceVariant,
                                onPrimary = AppColors.Light.OnPrimary,
                                cardBackground = AppColors.Light.CardBackground,
                                cardElevated = AppColors.Light.CardElevated,
                                success = AppColors.Light.Success,
                                warning = AppColors.Light.Warning,
                                error = AppColors.Light.Error,
                                timerGood = AppColors.Light.TimerGood,
                                timerWarning = AppColors.Light.TimerWarning,
                                timerCritical = AppColors.Light.TimerCritical,
                                isLight = true
                            )
                        }

                        CompositionLocalProvider(
                            LocalAppTheme provides theme,
                            content = content
                        )
                    }
                    // Composition Local
                    val LocalAppTheme = compositionLocalOf<AppTheme> {
                        error("No AppTheme provided")
                    }
                    
                    @Composable
                    fun appTheme(): AppTheme = LocalAppTheme.current
                    
                    @OptIn(ExperimentalMaterial3Api::class, ExperimentalAnimationApi::class)
                    @Composable
                    fun AuthenticatorApp() {
                        // Theme Manager
                        val themeManager = remember { ThemeManager() }

                        ProvideAppTheme(themeManager = themeManager) {
                            val theme = appTheme()
                            
                            val currentThemeMode by themeManager.themeMode.collectAsState()
                            
                            //Set theme
                            //themeManager.setThemeMode()
                            
                            //theme.timerGood
                        }
                    }
                    
                    //2 way use
                     val themeColors = if (isSystemInDarkTheme()) darkThemeColors else lightThemeColors

                     CompositionLocalProvider(LocalAppTheme provides themeColors) {
                         val theme = LocalAppTheme.current
                     }
                    """.trimIndent()
            )

            SpanText(
                text = "CompositionLocalProvider",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/flyfishxu/vetra-ui.git
            KotlinCode(
                code = """
                    //1 VetraColorScheme.kt
                    
                    import androidx.compose.runtime.Stable
                    import androidx.compose.runtime.staticCompositionLocalOf
                    import androidx.compose.ui.graphics.Color

                    /**
                     * Vetra Color Scheme
                     *
                     * A modern, elegant color system designed for universal appeal.
                     * Unlike Material Design's complex color roles, Vetra uses intuitive semantic naming
                     * that makes it easier for developers to understand and use.
                     *
                     * Design Philosophy:
                     * - Semantic naming over technical jargon
                     * - Clear visual hierarchy
                     * - Accessibility first
                     * - Beautiful by default
                     *
                     * @property brand Primary brand color - used for main interactive elements
                     * @property onBrand Content color on brand backgrounds
                     * @property brandSubtle Subtle variant of brand color for less emphasis
                     * @property onBrandSubtle Content color on subtle brand backgrounds
                     *
                     * @property accent Secondary accent color - for highlights and special states
                     * @property onAccent Content color on accent backgrounds
                     * @property accentSubtle Subtle variant of accent color
                     * @property onAccentSubtle Content color on subtle accent backgrounds
                     *
                     * @property canvas Background color - the main canvas of the app
                     * @property onCanvas Primary text/content color on canvas
                     * @property canvasElevated Elevated surface color (cards, dialogs)
                     * @property onCanvasElevated Content color on elevated surfaces
                     * @property canvasSubtle Subtle background for less emphasis
                     * @property onCanvasSubtle Content color on subtle backgrounds
                     *
                     * @property textPrimary Primary text color - for main content
                     * @property textSecondary Secondary text color - for supporting content
                     * @property textTertiary Tertiary text color - for hints and placeholders
                     * @property textDisabled Disabled text color
                     *
                     * @property border Border color for outlines and dividers
                     * @property borderSubtle Subtle border color
                     * @property borderFocus Focus ring color for accessibility
                     *
                     * @property success Success state color
                     * @property onSuccess Content color on success backgrounds
                     * @property warning Warning state color
                     * @property onWarning Content color on warning backgrounds
                     * @property danger Error/danger state color
                     * @property onDanger Content color on danger backgrounds
                     * @property info Information state color
                     * @property onInfo Content color on info backgrounds
                     *
                     * @property overlay Overlay/scrim color for modals
                     * @property shadow Shadow color
                     */
                    @Stable
                    data class VetraColorScheme(
                        // Brand Colors
                        val brand: Color,
                        val onBrand: Color,
                        val brandSubtle: Color,
                        val onBrandSubtle: Color,
                        val brandDisabled: Color,          // Desaturated brand for disabled states
                        val onBrandDisabled: Color,        // Content on disabled brand

                        // Accent Colors
                        val accent: Color,
                        val onAccent: Color,
                        val accentSubtle: Color,
                        val onAccentSubtle: Color,
                        val accentDisabled: Color,         // Desaturated accent for disabled states
                        val onAccentDisabled: Color,       // Content on disabled accent

                        // Canvas/Background Colors
                        val canvas: Color,
                        val onCanvas: Color,
                        val canvasElevated: Color,
                        val onCanvasElevated: Color,
                        val canvasSubtle: Color,
                        val onCanvasSubtle: Color,

                        // Text Colors
                        val textPrimary: Color,
                        val textSecondary: Color,
                        val textTertiary: Color,
                        val textDisabled: Color,

                        // Border Colors
                        val border: Color,
                        val borderSubtle: Color,
                        val borderFocus: Color,

                        // Semantic Colors
                        val success: Color,
                        val onSuccess: Color,
                        val warning: Color,
                        val onWarning: Color,
                        val danger: Color,
                        val onDanger: Color,
                        val dangerDisabled: Color,         // Desaturated danger for disabled states
                        val onDangerDisabled: Color,       // Content on disabled danger
                        val info: Color,
                        val onInfo: Color,

                        // Utility Colors
                        val overlay: Color,
                        val shadow: Color,
                    )

                    /**
                     * Light color scheme with modern, clean aesthetics
                     */
                    val VetraLightColorScheme = VetraColorScheme(
                        // Brand Colors - Modern Blue
                        brand = Color(0xFF2563EB),           // Vibrant, trustworthy blue
                        onBrand = Color(0xFFFFFFFF),
                        brandSubtle = Color(0xFFEFF6FF),     // Very light blue
                        onBrandSubtle = Color(0xFF1E40AF),
                        brandDisabled = Color(0xFFBFDBFE),   // Desaturated blue with good contrast
                        onBrandDisabled = Color(0xFF64748B), // Muted slate for readable text

                        // Accent Colors - Elegant Purple
                        accent = Color(0xFF7C3AED),          // Rich purple for highlights
                        onAccent = Color(0xFFFFFFFF),
                        accentSubtle = Color(0xFFF5F3FF),    // Very light purple
                        onAccentSubtle = Color(0xFF5B21B6),
                        accentDisabled = Color(0xFFDDD6FE),  // Desaturated purple with good contrast
                        onAccentDisabled = Color(0xFF64748B),// Muted slate for readable text

                        // Canvas Colors - Neutral Grays (enhanced contrast)
                        canvas = Color(0xFFF8F8F8),          // Slightly darker for better contrast
                        onCanvas = Color(0xFF0A0A0A),
                        canvasElevated = Color(0xFFFFFFFF),  // Pure white for cards
                        onCanvasElevated = Color(0xFF0A0A0A),
                        canvasSubtle = Color(0xFFF0F0F0),    // More distinct from canvas
                        onCanvasSubtle = Color(0xFF171717),

                        // Text Colors
                        textPrimary = Color(0xFF0A0A0A),     // Near black for readability
                        textSecondary = Color(0xFF525252),   // Medium gray
                        textTertiary = Color(0xFFA3A3A3),    // Light gray
                        textDisabled = Color(0xFFD4D4D4),    // Very light gray

                        // Border Colors (enhanced visibility)
                        border = Color(0xFFD6D6D6),          // More visible border
                        borderSubtle = Color(0xFFE8E8E8),    // More distinct subtle border
                        borderFocus = Color(0xFF3B82F6),     // Blue focus ring

                        // Semantic Colors
                        success = Color(0xFF10B981),         // Fresh green
                        onSuccess = Color(0xFFFFFFFF),
                        warning = Color(0xFFF59E0B),         // Warm amber
                        onWarning = Color(0xFF000000),
                        danger = Color(0xFFEF4444),          // Clear red
                        onDanger = Color(0xFFFFFFFF),
                        dangerDisabled = Color(0xFFFECDD3),  // Desaturated pink-red with good contrast
                        onDangerDisabled = Color(0xFF64748B),// Muted slate for readable text
                        info = Color(0xFF06B6D4),            // Bright cyan
                        onInfo = Color(0xFFFFFFFF),

                        // Utility Colors
                        overlay = Color(0x80000000),         // 50% black
                        shadow = Color(0x1A000000),          // 10% black
                    )

                    /**
                     * Dark color scheme with elegant, comfortable aesthetics
                     */
                    val VetraDarkColorScheme = VetraColorScheme(
                        // Brand Colors - Brighter for dark mode
                        brand = Color(0xFF3B82F6),           // Slightly brighter blue
                        onBrand = Color(0xFFFFFFFF),
                        brandSubtle = Color(0xFF1E293B),     // Dark blue-gray
                        onBrandSubtle = Color(0xFF60A5FA),
                        brandDisabled = Color(0xFF1E3A5F),   // Desaturated dark blue with good contrast
                        onBrandDisabled = Color(0xFF94A3B8), // Muted light slate

                        // Accent Colors - Vivid for dark mode
                        accent = Color(0xFF8B5CF6),          // Brighter purple
                        onAccent = Color(0xFFFFFFFF),
                        accentSubtle = Color(0xFF2E1065),    // Dark purple
                        onAccentSubtle = Color(0xFFA78BFA),
                        accentDisabled = Color(0xFF3B2463),  // Desaturated dark purple with good contrast
                        onAccentDisabled = Color(0xFF94A3B8),// Muted light slate

                        // Canvas Colors - Rich blacks (enhanced contrast)
                        canvas = Color(0xFF0F0F0F),          // Slightly lighter for better contrast
                        onCanvas = Color(0xFFFAFAFA),
                        canvasElevated = Color(0xFF1A1A1A),  // More distinct elevation
                        onCanvasElevated = Color(0xFFFAFAFA),
                        canvasSubtle = Color(0xFF080808),    // Darker than canvas
                        onCanvasSubtle = Color(0xFFE5E5E5),

                        // Text Colors
                        textPrimary = Color(0xFFFAFAFA),     // Near white for readability
                        textSecondary = Color(0xFFA3A3A3),   // Medium gray
                        textTertiary = Color(0xFF525252),    // Darker gray
                        textDisabled = Color(0xFF404040),    // Very dark gray

                        // Border Colors (enhanced visibility)
                        border = Color(0xFF2E2E2E),          // More visible border
                        borderSubtle = Color(0xFF242424),    // More distinct subtle border
                        borderFocus = Color(0xFF60A5FA),     // Brighter blue focus ring

                        // Semantic Colors - Adjusted for dark mode
                        success = Color(0xFF34D399),         // Brighter green
                        onSuccess = Color(0xFF000000),
                        warning = Color(0xFFFBBF24),         // Brighter amber
                        onWarning = Color(0xFF000000),
                        danger = Color(0xFFF87171),          // Softer red
                        onDanger = Color(0xFF000000),
                        dangerDisabled = Color(0xFF4C2020),  // Desaturated dark red with good contrast
                        onDangerDisabled = Color(0xFF94A3B8),// Muted light slate
                        info = Color(0xFF22D3EE),            // Brighter cyan
                        onInfo = Color(0xFF000000),

                        // Utility Colors
                        overlay = Color(0xCC000000),         // 80% black (darker for dark mode)
                        shadow = Color(0x4D000000),          // 30% black (more visible)
                    )

                    /**
                     * Local provider for Vetra color scheme
                     */
                    val LocalVetraColorScheme = staticCompositionLocalOf { VetraLightColorScheme }
                    
                    //2 VetraShadows.kt
                    
                    /**
                     * Vetra Shadow System
                     *
                     * A refined elevation system using soft, natural shadows that create
                     * comfortable visual hierarchy without being harsh or distracting.
                     *
                     * Design Philosophy:
                     * - Clear, visible shadows that establish proper depth hierarchy
                     * - Multiple blur levels for realistic depth perception
                     * - Adaptive transparency for light and dark modes
                     * - Optimized for cross-platform rendering
                     */
                    @Stable
                    data class VetraShadows(
                        /**
                         * None - 0dp
                         * No elevation, flat surface
                         */
                        val none: Dp,

                        /**
                         * XS - 1dp
                         * Barely perceptible, for subtle separation
                         * Use for: Hovering elements, subtle depth
                         */
                        val xs: Dp,

                        /**
                         * SM - 2dp
                         * Light elevation for contained elements
                         * Use for: Cards, contained buttons
                         */
                        val sm: Dp,

                        /**
                         * MD - 4dp
                         * Moderate elevation for interactive elements
                         * Use for: Raised buttons, floating action buttons
                         */
                        val md: Dp,

                        /**
                         * LG - 8dp
                         * Strong elevation for prominent elements
                         * Use for: Dropdowns, tooltips, popovers
                         */
                        val lg: Dp,

                        /**
                         * XL - 16dp
                         * Maximum elevation for overlay elements
                         * Use for: Dialogs, bottom sheets, navigation drawers
                         */
                        val xl: Dp,

                        /**
                         * 2XL - 24dp
                         * Dramatic elevation for critical overlays
                         * Use for: Modals, critical alerts
                         */
                        val xxl: Dp,
                    )

                    /**
                     * Shadow configuration for different theme modes
                     *
                     * @param ambientAlpha Alpha value for ambient shadow (0.0 - 1.0)
                     * @param spotAlpha Alpha value for spot shadow (0.0 - 1.0)
                     * @param elevationMultiplier Multiplier for elevation spread (typically 1.0 - 1.5)
                     */
                    @Immutable
                    data class ShadowConfig(
                        val ambientAlpha: Float,
                        val spotAlpha: Float,
                        val elevationMultiplier: Float = 1.2f
                    )

                    /**
                     * Default Vetra Shadows
                     */
                    val DefaultVetraShadows = VetraShadows(
                        none = 0.dp,
                        xs = 1.dp,
                        sm = 2.dp,
                        md = 4.dp,
                        lg = 8.dp,
                        xl = 16.dp,
                        xxl = 24.dp,
                    )

                    /**
                     * Light mode shadow configuration
                     */
                    val LightModeShadowConfig = ShadowConfig(
                        ambientAlpha = 0.15f,  // 15% - Soft ambient shadow for gentle depth
                        spotAlpha = 0.25f,     // 25% - Clear directional shadow for separation
                        elevationMultiplier = 1.15f  // Conservative spread for natural appearance
                    )

                    /**
                     * Dark mode shadow configuration
                     */
                    val DarkModeShadowConfig = ShadowConfig(
                        ambientAlpha = 0.25f,  // 25% - Stronger ambient for dark background visibility
                        spotAlpha = 0.35f,     // 35% - Clear directional shadow on dark backgrounds
                        elevationMultiplier = 1.2f  // Slightly more spread for dark mode visibility
                    )

                    /**
                     * Soft, natural shadow
                     *
                     * Design principles:
                     * - Shadows should be clearly visible and establish hierarchy
                     * - Heavier at the bottom (natural light from above)
                     * - Adaptive opacity for different theme modes
                     * - Balanced spread for natural appearance
                     *
                     * @param elevation The elevation level (vertical distance from surface)
                     * @param shape The shape of the shadow
                     * @param clip Whether to clip the content to the shape
                     */
                    fun Modifier.vetraShadow(
                        elevation: Dp,
                        shape: Shape = RectangleShape,
                        clip: Boolean = false
                    ): Modifier = composed {
                        if (elevation <= 0.dp) {
                            return@composed this
                        }

                        val config = LocalVetraShadowConfig.current

                        // Apply configured elevation multiplier for natural shadow spread
                        val effectiveElevation = elevation * config.elevationMultiplier

                        // Use configured alpha values for proper visibility
                        val ambientColor = Color.Black.copy(alpha = config.ambientAlpha)
                        val spotColor = Color.Black.copy(alpha = config.spotAlpha)

                        this.shadow(
                            elevation = effectiveElevation,
                            shape = shape,
                            clip = clip,
                            ambientColor = ambientColor,
                            spotColor = spotColor
                        )
                    }

                    /**
                     * Local provider for Vetra shadows
                     */
                    val LocalVetraShadows = staticCompositionLocalOf { DefaultVetraShadows }

                    /**
                     * Local provider for shadow configuration
                     * Automatically adapts to light/dark mode
                     */
                    val LocalVetraShadowConfig = staticCompositionLocalOf { LightModeShadowConfig }
                    
                    //3 VetraShapes.kt
                    
                    /**
                     * Vetra Shape System
                     *
                     * A flexible corner radius system for creating visual hierarchy and comfort.
                     * Uses a balanced scale that works across different component sizes.
                     *
                     * Philosophy:
                     * - Smooth, comfortable corners
                     * - Clear size-appropriate rounding
                     * - Consistent visual rhythm
                     * - Elegant without being excessive
                     */
                    @Stable
                    data class VetraShapes(
                        /**
                         * None - 0dp
                         * Sharp corners for specific design needs
                         */
                        val none: Shape,

                        /**
                         * Extra Small - 4dp
                         * For small elements like badges and chips
                         */
                        val xs: Shape,

                        /**
                         * Small - 8dp
                         * For buttons, inputs, and small components
                         */
                        val sm: Shape,

                        /**
                         * Medium - 12dp
                         * For cards and medium components
                         */
                        val md: Shape,

                        /**
                         * Large - 16dp
                         * For large cards and containers
                         */
                        val lg: Shape,

                        /**
                         * Extra Large - 24dp
                         * For dialogs and bottom sheets
                         */
                        val xl: Shape,

                        /**
                         * 2XL - 32dp
                         * For full-screen modals
                         */
                        val xxl: Shape,

                        /**
                         * Full - 9999dp
                         * For circular/pill-shaped elements
                         */
                        val full: Shape,
                    )

                    /**
                     * Default Vetra Shapes
                     */
                    val DefaultVetraShapes = VetraShapes(
                        none = RoundedCornerShape(0.dp),
                        xs = RoundedCornerShape(4.dp),
                        sm = RoundedCornerShape(8.dp),
                        md = RoundedCornerShape(12.dp),
                        lg = RoundedCornerShape(16.dp),
                        xl = RoundedCornerShape(24.dp),
                        xxl = RoundedCornerShape(32.dp),
                        full = RoundedCornerShape(9999.dp),
                    )

                    /**
                     * Local provider for Vetra shapes
                     */
                    val LocalVetraShapes = staticCompositionLocalOf { DefaultVetraShapes }
                    
                    //4 VetraTypography.kt
                    
                    /**
                     * Vetra Typography System
                     *
                     * A modern, elegant typography scale designed for clarity and readability.
                     * Uses a harmonious size progression and careful line height ratios.
                     *
                     * Philosophy:
                     * - Clear hierarchy through size and weight
                     * - Generous line heights for readability
                     * - Careful letter spacing for elegance
                     * - Optimized for both display and reading
                     *
                     * Size Scale (based on major second - 1.125):
                     * - 11sp, 12sp, 14sp, 16sp, 18sp, 20sp, 24sp, 28sp, 32sp, 40sp, 48sp
                     */
                    @Stable
                    data class VetraTypography(
                        /**
                         * Display Extra Large - 48sp
                         * For hero sections and major headings
                         */
                        val displayXl: TextStyle,

                        /**
                         * Display Large - 40sp
                         * For page titles and major sections
                         */
                        val displayLg: TextStyle,

                        /**
                         * Display Medium - 32sp
                         * For prominent headings
                         */
                        val displayMd: TextStyle,

                        /**
                         * Display Small - 28sp
                         * For section headings
                         */
                        val displaySm: TextStyle,

                        /**
                         * Heading Extra Large - 24sp
                         * For card/component titles
                         */
                        val headingXl: TextStyle,

                        /**
                         * Heading Large - 20sp
                         * For subsection headings
                         */
                        val headingLg: TextStyle,

                        /**
                         * Heading Medium - 18sp
                         * For list headers and labels
                         */
                        val headingMd: TextStyle,

                        /**
                         * Heading Small - 16sp
                         * For small headings and emphasized text
                         */
                        val headingSm: TextStyle,

                        /**
                         * Body Large - 16sp
                         * For main content and paragraphs
                         */
                        val bodyLg: TextStyle,

                        /**
                         * Body Medium - 14sp
                         * For secondary content and descriptions
                         */
                        val bodyMd: TextStyle,

                        /**
                         * Body Small - 12sp
                         * For captions and helper text
                         */
                        val bodySm: TextStyle,

                        /**
                         * Label Large - 14sp Bold
                         * For buttons and important labels
                         */
                        val labelLg: TextStyle,

                        /**
                         * Label Medium - 12sp Bold
                         * For form labels and tags
                         */
                        val labelMd: TextStyle,

                        /**
                         * Label Small - 11sp Bold
                         * For badges and small labels
                         */
                        val labelSm: TextStyle,
                    )

                    /**
                     * Default Vetra Typography
                     */
                    val DefaultVetraTypography = VetraTypography(
                        // Display Styles - Bold, tight spacing
                        displayXl = TextStyle(
                            fontSize = 48.sp,
                            fontWeight = FontWeight.Bold,
                            lineHeight = 56.sp,
                            letterSpacing = (-0.5).sp
                        ),
                        displayLg = TextStyle(
                            fontSize = 40.sp,
                            fontWeight = FontWeight.Bold,
                            lineHeight = 48.sp,
                            letterSpacing = (-0.5).sp
                        ),
                        displayMd = TextStyle(
                            fontSize = 32.sp,
                            fontWeight = FontWeight.Bold,
                            lineHeight = 40.sp,
                            letterSpacing = (-0.25).sp
                        ),
                        displaySm = TextStyle(
                            fontSize = 28.sp,
                            fontWeight = FontWeight.Bold,
                            lineHeight = 36.sp,
                            letterSpacing = 0.sp
                        ),

                        // Heading Styles - SemiBold, balanced
                        headingXl = TextStyle(
                            fontSize = 24.sp,
                            fontWeight = FontWeight.SemiBold,
                            lineHeight = 32.sp,
                            letterSpacing = 0.sp
                        ),
                        headingLg = TextStyle(
                            fontSize = 20.sp,
                            fontWeight = FontWeight.SemiBold,
                            lineHeight = 28.sp,
                            letterSpacing = 0.sp
                        ),
                        headingMd = TextStyle(
                            fontSize = 18.sp,
                            fontWeight = FontWeight.SemiBold,
                            lineHeight = 26.sp,
                            letterSpacing = 0.sp
                        ),
                        headingSm = TextStyle(
                            fontSize = 16.sp,
                            fontWeight = FontWeight.SemiBold,
                            lineHeight = 24.sp,
                            letterSpacing = 0.sp
                        ),

                        // Body Styles - Regular, generous line height
                        bodyLg = TextStyle(
                            fontSize = 16.sp,
                            fontWeight = FontWeight.Normal,
                            lineHeight = 24.sp,
                            letterSpacing = 0.15.sp
                        ),
                        bodyMd = TextStyle(
                            fontSize = 14.sp,
                            fontWeight = FontWeight.Normal,
                            lineHeight = 20.sp,
                            letterSpacing = 0.1.sp
                        ),
                        bodySm = TextStyle(
                            fontSize = 12.sp,
                            fontWeight = FontWeight.Normal,
                            lineHeight = 16.sp,
                            letterSpacing = 0.1.sp
                        ),

                        // Label Styles - Medium/SemiBold, uppercase-ready
                        labelLg = TextStyle(
                            fontSize = 14.sp,
                            fontWeight = FontWeight.SemiBold,
                            lineHeight = 20.sp,
                            letterSpacing = 0.1.sp
                        ),
                        labelMd = TextStyle(
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                            lineHeight = 16.sp,
                            letterSpacing = 0.5.sp
                        ),
                        labelSm = TextStyle(
                            fontSize = 11.sp,
                            fontWeight = FontWeight.SemiBold,
                            lineHeight = 16.sp,
                            letterSpacing = 0.5.sp
                        ),
                    )

                    /**
                     * Local provider for Vetra typography
                     */
                    val LocalVetraTypography = staticCompositionLocalOf { DefaultVetraTypography }
                    
                    // VetraTheme.kt
                    
                    **
                     * Vetra Theme
                     *
                     * The main theming composable for Vetra UI. Provides a complete design system
                     * independent from Material Design, with its own color, typography, shape, and shadow systems.
                     *
                     * Philosophy:
                     * - Independent design system (not based on Material)
                     * - Intuitive, semantic naming
                     * - Beautiful by default
                     * - Easy to customize
                     * - Accessible and modern
                     *
                     * @param darkMode Whether to use dark mode
                     * @param colors Custom color scheme (defaults to built-in light/dark schemes)
                     * @param typography Custom typography (defaults to DefaultVetraTypography)
                     * @param shapes Custom shapes (defaults to DefaultVetraShapes)
                     * @param shadows Custom shadows (defaults to DefaultVetraShadows)
                     * @param shadowConfig Custom shadow configuration (defaults based on darkMode)
                     * @param content The content to theme
                     */
                    @Composable
                    fun VetraTheme(
                        darkMode: Boolean = false,
                        colors: VetraColorScheme = if (darkMode) VetraDarkColorScheme else VetraLightColorScheme,
                        typography: VetraTypography = DefaultVetraTypography,
                        shapes: VetraShapes = DefaultVetraShapes,
                        shadows: VetraShadows = DefaultVetraShadows,
                        shadowConfig: ShadowConfig = if (darkMode) DarkModeShadowConfig else LightModeShadowConfig,
                        content: @Composable () -> Unit
                    ) {
                        CompositionLocalProvider(
                            LocalVetraColorScheme provides colors,
                            LocalVetraTypography provides typography,
                            LocalVetraShapes provides shapes,
                            LocalVetraShadows provides shadows,
                            LocalVetraShadowConfig provides shadowConfig,
                            content = content
                        )
                    }

                    /**
                     * Contains functions to access the current theme values
                     */
                    object VetraTheme {
                        /**
                         * Retrieves the current [VetraColorScheme] at the call site's position in the hierarchy.
                         */
                        val colors: VetraColorScheme
                            @Composable
                            @ReadOnlyComposable
                            get() = LocalVetraColorScheme.current

                        /**
                         * Retrieves the current [VetraTypography] at the call site's position in the hierarchy.
                         */
                        val typography: VetraTypography
                            @Composable
                            @ReadOnlyComposable
                            get() = LocalVetraTypography.current

                        /**
                         * Retrieves the current [VetraShapes] at the call site's position in the hierarchy.
                         */
                        val shapes: VetraShapes
                            @Composable
                            @ReadOnlyComposable
                            get() = LocalVetraShapes.current

                        /**
                         * Retrieves the current [VetraShadows] at the call site's position in the hierarchy.
                         */
                        val shadows: VetraShadows
                            @Composable
                            @ReadOnlyComposable
                            get() = LocalVetraShadows.current

                        /**
                         * Retrieves the current [ShadowConfig] at the call site's position in the hierarchy.
                         */
                        val shadowConfig: ShadowConfig
                            @Composable
                            @ReadOnlyComposable
                            get() = LocalVetraShadowConfig.current
                    }
                    """.trimIndent()
            )

            Ul {
                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "CompositionLocal - The Broadcast Channel: Think of this as a named radio frequency. It doesn’t hold any data itself — it’s just an identifier, a key, a “channel number.”",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "CompositionLocalProvider - The Radio Tower: This is what actually broadcasts the value. It says: “Hey, for everyone below me in the tree, when you tune into LocalThemeColor, you'll get Color.Red.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "CompositionLocalProvider helps bind your CompositionLocal with its value. CompositionLocalProvider can provide a reference to some object from a higher level in the tree, and any child can access the provided value directly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LocalNavigationProvider is the name of your own CompositionLocal. provides is the infix function that you call to assign the default value to your CompositionLocal.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }
            }

            SpanText(
                text = "How CompositionLocal Fits In",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you call CompositionLocalProvider:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Compose records the binding — At this point in the tree, LocalThemeColor = Color.Red",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Children inherit by default — Every composable below this point can see this binding",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Lookup is hierarchical — When a child calls .current, Compose walks UP the tree until it finds a provider (or uses the default)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It’s like a family inheritance system. Children inherit from parents unless someone explicitly changes the inheritance.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    val navController = LocalNavigationProvider.current
                    """.trimIndent()
            )

            SpanText(
                text = "rememberNavController",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //1
                    CompositionLocalProvider(LocalNavigationProvider provides rememberNavController()) { }
                    
                    //2
                    val LocalNavController = compositionLocalOf<NavHostController> {
                        error("NavController not provided")
                    }

                    @Composable
                    fun AppNavigationHost() {
                        val navController = rememberNavController()

                        CompositionLocalProvider(LocalNavController provides navController) {
                            NavHost(
                                navController = navController,
                                startDestination = NavigationRoutes.LOGIN_ROUTE.name,
                                modifier = Modifier
                                    .background(Color.Transparent)
                                    .fillMaxSize(),
                                enterTransition = {
                                    slideIntoContainer(
                                        AnimatedContentTransitionScope.SlideDirection.Left,
                                        animationSpec = tween(300)
                                    )
                                },
                                exitTransition = {
                                    slideOutOfContainer(
                                        AnimatedContentTransitionScope.SlideDirection.Left,
                                        animationSpec = tween(300)
                                    )
                                },
                                popEnterTransition = {
                                    slideIntoContainer(
                                        AnimatedContentTransitionScope.SlideDirection.Right,
                                        animationSpec = tween(300)
                                    )
                                },
                                popExitTransition = {
                                    slideOutOfContainer(
                                        AnimatedContentTransitionScope.SlideDirection.Right,
                                        animationSpec = tween(300)
                                    )
                                }
                            ) {
                                
                            }
                        }
                    }
                    
                    //Access
                    val navigator = LocalNavController.current
                    navigator.navigate(Route Name)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    private val BottomNavHeight = 56.dp

                    private val BottomNavHeightLocal = compositionLocalOf { 0.dp }

                    val WindowInsets.Companion.bottomNav
                        @Composable get() = WindowInsets(bottom = BottomNavHeightLocal.current)

                    @Composable
                    fun WithBottomNav(
                        navigator: Navigator,
                        content: @Composable () -> Unit,
                    ) {
                        Box {
                            val isBottomNavVisible = navigator.size <= 1
                            CompositionLocalProvider(
                                BottomNavHeightLocal provides if (isBottomNavVisible) BottomNavHeight else 0.dp,
                                content = content,
                            )
                            AnimatedVisibility(
                                isBottomNavVisible,
                                enter = slideInVertically(initialOffsetY = { it }),
                                exit = slideOutVertically(targetOffsetY = { it }),
                                modifier = Modifier.align(Alignment.BottomCenter),
                            ) {
                                BottomNav(navigator)
                            }
                        }
                    }

                    @Composable
                    fun BottomNav(navigator: Navigator) {
                        BottomNavigation {
                            TopLevelMenus.entries.fastForEach {
                                val isSelected = it.isSelected(navigator)
                                BottomNavigationItem(
                                    selected = isSelected,
                                    onClick = {
                                        if (isSelected) return@BottomNavigationItem
                                        navigator replace it.createScreen()
                                    },
                                    icon = {
                                        if (isSelected) {
                                            Icon(it.selectedIcon, stringResource(it.textKey))
                                        } else {
                                            Icon(it.unselectedIcon, stringResource(it.textKey))
                                        }
                                    },
                                    label = {
                                        Text(
                                            text = stringResource(it.textKey),
                                            maxLines = 1,
                                            overflow = TextOverflow.Ellipsis,
                                        )
                                    },
                                    alwaysShowLabel = true,
                                    modifier = Modifier
                                        .windowInsetsPadding(
                                            WindowInsets.navigationBars.only(
                                                WindowInsetsSides.Horizontal + WindowInsetsSides.Bottom,
                                            ),
                                        )
                                        .height(BottomNavHeight),
                                )
                            }
                        }
                    }

                    @Composable
                    fun BottomNavigation(
                        modifier: Modifier = Modifier,
                        backgroundColor: Color = MaterialTheme.colors.primarySurface,
                        contentColor: Color = contentColorFor(backgroundColor),
                        elevation: Dp = BottomNavigationDefaults.Elevation,
                        content: @Composable RowScope.() -> Unit,
                    ) {
                        Surface(
                            color = backgroundColor,
                            contentColor = contentColor,
                            elevation = elevation,
                            modifier = modifier,
                        ) {
                            Row(
                                Modifier
                                    .fillMaxWidth()
                                    .defaultMinSize(minHeight = BottomNavHeight)
                                    .selectableGroup(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                content = content,
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                     isRtl: Boolean = false
                     
                    CompositionLocalProvider(
                        LocalLayoutDirection provides
                            if (isRtl) LayoutDirection.Rtl else LayoutDirection.Ltr
                    ) { }
                    """.trimIndent()
            )

            SpanText(
                text = "Interop Communication with CompositionLocal",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LocalFeedbackInterop is a staticCompositionLocalOf that will hold an implementation of the FeedbackMessageInterop interface. This interface defines the methods for showing feedback alerts, and the staticCompositionLocalOf ensures that the implementation is unlikely to change as it cannot be re-provided. This allows any composable within the scope to access the FeedbackMessageInterop implementation and trigger feedback messages without explicit dependencies.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                KotlinCode(
                    code = """
                    import androidx.compose.runtime.ProvidableCompositionLocal
                    import androidx.compose.runtime.staticCompositionLocalOf
                    
                    //private val LocalColorScheme: ProvidableCompositionLocal<ColorScheme> = compositionLocalOf { lightColorScheme() }
                    val LocalFeedbackInterop: ProvidableCompositionLocal<FeedbackMessageInterop?> = staticCompositionLocalOf { null }

                    interface FeedbackMessageInterop {
                        fun showError(message: String)
                        fun showSuccess(message: String)
                        fun showInfo(message: String)
                    }
                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "From our Compose code, we can retrieve the instance of the CompositionLocal to access the FeedbackMessageInterop implementation and trigger our feedback message View-based component that rely on an error state collected from a ViewModel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .textAlign(TextAlign.Start)
                    )
                }

                KotlinCode(
                    code = """
                        @Composable
                        fun Content() {
                            val feedbackHost = LocalFeedbackInterop.current

                            LaunchedEffect(feedbackHost) {
                                viewModel.error.collect {
                                    feedbackHost?.showError(
                                        message = "My error message",
                                    )
                                }
                            }
                        }
                    """.trimIndent()
                )
            }

            SpanText(
                text = "How to Extend Material Theme with Your Own Colors?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Material Theme provides colors like primary, secondary, and error. But what if you want to add your own colors to the theme? We can use Kotlin’s extension functions for that.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Simply add your color to the ColorScheme class, and you can access it globally in your code.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                            .textAlign(TextAlign.Start)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Generally, we hardcode colors, but this approach is better because:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                            .textAlign(TextAlign.Start)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "You can handle Dark Mode logic right inside the property.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .fontWeight(FontWeight.Bold)
                                    .textAlign(TextAlign.Start)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "You don't need to import random constants. If you have access to MaterialTheme, you have access to your brand colors.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .fontWeight(FontWeight.Bold)
                                    .textAlign(TextAlign.Start)
                            )
                        }
                    }
                }
            }

            KotlinCode(
                code = """
                    //Add your colors as extension properties 
                    val ColorScheme.successGreen: Color
                    @Composable get() = if (isSystemInDarkTheme()) Color(0xFF00853) else Color(0xFF2E7D32)
                    
                    Text(
                        text = "Success!"
                        color = MaterialTheme.colorScheme.successGreen // Clean
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "compositionLocalOf() Vs staticCompositionLocalOf()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "compositionLocalOf()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                            .textAlign(TextAlign.Start)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Behavior: Tracks every reader.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Performance: If the value changes, only the specific composables reading it will re-compose.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Data that changes frequently (User state, dynamic colors).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Use for dynamic values that change at runtime (e.g., dark mode toggle). Triggers recomposition when updated. It will subscribe accurately and the value will change accurately Recompose, so the performance consumption is in the subscription stage.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Suitable for scenarios where values may change frequently.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Compile-time optimized.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Doesn't track reads dynamically",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "All consumers recompose when value changes.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "staticCompositionLocalOf()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                            .textAlign(TextAlign.Start)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Behavior: Does NOT track readers.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Performance: If the value changes, the entire Ul tree underneath is rebuilt.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Stable data (Fixed themes, spacing constants).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Use for rarely changing values (e.g., typography). Does NOT trigger recomposition, improving performance. It does not support subscription or full data when value changes Recompose, so the performance consumption is in the update phase.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Suitable for scenarios where the value will not change or will hardly change . For example compose, LocalView",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Tracks reads at runtime.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Only recomposes the places that actually use the value.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Use when values are conditionally read or change frequently.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .textAlign(TextAlign.Start)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    expect class WavePlayer() {
                        fun play(url: String)
                    }

                    @Composable
                    fun ProvideWavePlayer(
                        content: @Composable () -> Unit
                    ) {
                        val player = remember { WavePlayer() }
                        CompositionLocalProvider(LocalWavePlayer provides player) {
                            content()
                        }
                    }

                    @Composable
                    fun PlayWave(url: String) {
                        val player = LocalWavePlayer.current

                        LaunchedEffect(url) {
                            player.play(url)
                        }
                    }

                    val LocalWavePlayer = staticCompositionLocalOf<WavePlayer> { error("WavePlayer not initialized") }
                    """.trimIndent()
            )

            //Ripple Effect
            KotlinCode(
                code = """
                    @Composable
                    private fun BottomSaveActionButton(
                        modifier: Modifier = Modifier,
                        show: Boolean,
                        isSaving: Boolean,
                        saveAction: () -> Unit,
                    ) {

                        AnimatedVisibility(
                            modifier = modifier
                                .fillMaxWidth(),
                            visible = show,
                            enter = fadeIn() + expandVertically(),
                            exit = fadeOut() + shrinkVertically(),
                        ) {
                            ButtonRegular(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(start = 28.dp, end = 28.dp, top = 12.dp, bottom = 20.dp),
                                isLoading = isSaving,
                                text = stringResource(Res.string.common_save),
                            ) {
                                saveAction()
                            }
                        }
                    }
                    
                    enum class ButtonRegularSize {
                        DEFAULT,
                        MEDIUM
                    }

                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun ButtonRegular(
                        modifier: Modifier = Modifier,
                        text: String,
                        enabled: Boolean = true,
                        isLoading: Boolean = false,
                        wrapContentWidth: Boolean = false,
                        size: ButtonRegularSize = ButtonRegularSize.DEFAULT,
                        color: Color = ,
                        contentColor: Color = ,
                        disabledColor: Color = ,
                        disabledContentColor: Color = ,
                        onClick: () -> Unit,
                    ) {
                        val height = remember(size) {
                            when (size) {
                                ButtonRegularSize.DEFAULT -> 48.dp
                                ButtonRegularSize.MEDIUM -> 36.dp
                            }
                        }
                        val ripple = RippleConfiguration(color = AppColor.White)

                        CompositionLocalProvider(LocalRippleConfiguration provides ripple) {
                            Button(
                                onClick = onClick,
                                modifier = modifier
                                    .let {
                                        if (wrapContentWidth) {
                                            it.wrapContentWidth()
                                        } else {
                                            it.fillMaxWidth()
                                        }
                                    }
                                    .height(height)
                                    .defaultMinSize(minHeight = height),
                                shape = RoundedCornerShape(10.dp),
                                enabled = enabled && !isLoading,
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = color,
                                    contentColor = contentColor,
                                    disabledContainerColor = if (isLoading) color else disabledColor,
                                    disabledContentColor = disabledContentColor,
                                ),
                            ) {
                                if (!isLoading) {
                                    Text(
                                        text = text,
                                        style = RubikMedium16(),
                                        color = if (enabled) { contentColor } else { disabledContentColor },
                                    )
                                } else {
                                    CircularProgressIndicator(
                                        modifier = Modifier.size(24.dp),
                                        color = contentColor,
                                    )
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    val LocalUsername = compositionLocalOf { "Guest" }
                    
                    val defaultUsername = LocalUsername.current
                    
                    Column {
                        // Outside CompositionLocalProvider - uses default value
                        Text("Hello $defaultUsername")
                    
                        CompositionLocalProvider(LocalUsername provides "Kamlesh") {
                            // Inside CompositionLocalProvider - overridden value is used
                            Text("Inside scope: ${LocalUsername.current)")
                        }
                        
                        // Outside again - reverts back to default
                        Text ("Outside scope:  $defaultUsername")
                    }
                    
                    
                    //Hello Guest
                    //Inside scope: Kamlesh
                    //Outside scope: Guest
                    """.trimIndent()
            )

            //You want background text measurement
            //You need non-UI thread work scoped to composition
            //You want dependency injection without ViewModel

            //Tips Smart Work
            KotlinCode(
                code = """
                    //Background text prefetching is a pre-warming optimization in Compose that moves the costly word measurement/cache preparation performed by the Android framework during layout outside the UI thread. It is narrow in scope but can reduce jank when used appropriately.
                    
                    @Composable
                    fun PrefetchText(
                        modifier: Modifier = Modifier,
                        content: @Composable () -> Unit
                    ) {
                        val textMeasureExecutor = remember {
                            Executors.newSingleThreadExecutor()
                        }

                        // Clean up executor when composable leaves composition
                        DisposableEffect(Unit) {
                            onDispose {
                                textMeasureExecutor.shutdown()
                            }
                        }

                        CompositionLocalProvider(
                            LocalBackgroundTextMeasurementExecutor provides textMeasureExecutor
                        ) {
                            content()
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val fontFamilyResolver = LocalFontFamilyResolver.current

                    val resolvedTypeface =
                        remember(textStyle) { fontFamilyResolver.resolve(textStyle.fontFamily) }.value
                        
                    TextStyle(
                        fontFamily = FontFamily(resolvedTypeface as? Typeface ?: Typeface.DEFAULT)
                    )
                    """.trimIndent()
            )

            //Tips Smart Work
            //https://github.com/patrykandpatrick/liftapp.git
            KotlinCode(
                code = $$"""
                    val formatter = LocalFormatter.current
                    
                    <string name="exercise_set_format_weight">%1$s × %2$s</string>
                    stringResource(
                        R.string.exercise_set_format_weight,
                        formatter.formatValue(weight, weightUnit),
                        formatter.formatValue(reps, RepsUnit),
                    )
                    
                    <string name="exercise_set_format_cardio">%1$s ꞏ %2$s ꞏ %3$s</string>
                    stringResource(
                        R.string.exercise_set_format_cardio,
                        formatter.formatDuration(duration),
                        formatter.formatValue(distance, distanceUnit),
                        formatter.formatValue(kcal, EnergyUnit.KiloCalorie),
                    )
                    """.trimIndent()
            )

            //https://github.com/RoyalTaxi/yalla-components.git
            KotlinCode(
                code = """
                    //Custom Shape, Color, Dimensions Set
                    
                    @Composable
                    fun AddressCard(
                        state: AddressCardState,
                        onClick: () -> Unit,
                        modifier: Modifier = Modifier,
                        icon: Painter? = null,
                        colors: AddressCardDefaults.AddressCardColors = AddressCardDefaults.colors(),
                        dimens: AddressCardDefaults.AddressCardDimens = AddressCardDefaults.dimens(),
                    ) { }
                    
                    /**
                     * Default values for [AddressCard].
                     *
                     * Provides theme-aware defaults for [colors] and [dimens] that can be overridden.
                     */
                    object AddressCardDefaults {

                        /**
                         * Color configuration for [AddressCard].
                         *
                         * @param container Background color.
                         * @param name Name text color.
                         * @param address Address text color.
                         * @param metadata Metadata text color.
                         */
                        data class AddressCardColors(
                            val container: Color,
                            val name: Color,
                            val address: Color,
                            val metadata: Color,
                        )

                        @Composable
                        fun colors(
                            container: Color = System.color.backgroundSecondary,
                            name: Color = System.color.textBase,
                            address: Color = System.color.textBase,
                            metadata: Color = System.color.textBase,
                        ): AddressCardColors = AddressCardColors(
                            container = container,
                            name = name,
                            address = address,
                            metadata = metadata,
                        )

                        /**
                         * Dimension configuration for [AddressCard].
                         *
                         * @param shape Card corner shape.
                         * @param maxWidth Maximum card width.
                         * @param height Card height.
                         * @param iconSpacing Spacing after icon.
                         * @param contentSpacing Spacing between content elements.
                         * @param contentPadding Padding around content.
                         */
                        data class AddressCardDimens(
                            val shape: Shape,
                            val maxWidth: Dp,
                            val height: Dp,
                            val iconSpacing: Dp,
                            val contentSpacing: Dp,
                            val contentPadding: PaddingValues,
                        )

                        @Composable
                        fun dimens(
                            shape: Shape = RoundedCornerShape(20.dp),
                            maxWidth: Dp = 248.dp,
                            height: Dp = 120.dp,
                            iconSpacing: Dp = 8.dp,
                            contentSpacing: Dp = 8.dp,
                            contentPadding: PaddingValues = PaddingValues(vertical = 16.dp, horizontal = 20.dp),
                        ): AddressCardDimens = AddressCardDimens(
                            shape = shape,
                            maxWidth = maxWidth,
                            height = height,
                            iconSpacing = iconSpacing,
                            contentSpacing = contentSpacing,
                            contentPadding = contentPadding,
                        )
                    }
                    """.trimIndent()
            )

            //https://github.com/RoyalTaxi/yalla-components.git
            KotlinCode(
                code = """
                    
                    /**
                     * Default configuration values for [PrimaryField].
                     *
                     * Provides theme-aware defaults for [colors], [style], and [dimens] that can be overridden.
                     */
                    object PrimaryFieldDefaults {

                        /**
                         * Color configuration for [PrimaryField].
                         *
                         * @param focusedText Text color when focused.
                         * @param unfocusedText Text color when not focused.
                         * @param focusedBorder Border color when focused.
                         * @param unfocusedBorder Border color when not focused.
                         * @param cursor Cursor color.
                         * @param selection Text selection colors.
                         * @param placeholder Placeholder text color.
                         */
                        data class PrimaryFieldColors(
                            val focusedText: Color,
                            val unfocusedText: Color,
                            val focusedBorder: Color,
                            val unfocusedBorder: Color,
                            val cursor: Color,
                            val selection: TextSelectionColors,
                            val placeholder: Color
                        )

                        @Composable
                        fun colors(
                            focusedText: Color = System.color.textBase,
                            unfocusedText: Color = System.color.textBase,
                            focusedBorder: Color = System.color.borderFilled,
                            unfocusedBorder: Color = System.color.borderDisabled,
                            cursor: Color = System.color.textLink,
                            selection: TextSelectionColors = TextSelectionColors(
                                handleColor = System.color.textLink,
                                backgroundColor = System.color.textLink.copy(.3f)
                            ),
                            placeholder: Color = System.color.textSubtle
                        ) = PrimaryFieldColors(
                            focusedText = focusedText,
                            unfocusedText = unfocusedText,
                            focusedBorder = focusedBorder,
                            unfocusedBorder = unfocusedBorder,
                            cursor = cursor,
                            selection = selection,
                            placeholder = placeholder
                        )

                        /**
                         * Text style configuration for [PrimaryField].
                         *
                         * @param text Style applied to the input text.
                         * @param placeholder Style applied to the placeholder text.
                         */
                        data class PrimaryFieldStyle(
                            val text: TextStyle,
                            val placeholder: TextStyle
                        )

                        @Composable
                        fun style(
                            text: TextStyle = System.font.body.base.medium,
                            placeholder: TextStyle = System.font.body.base.medium
                        ) = PrimaryFieldStyle(
                            text = text,
                            placeholder = placeholder
                        )

                        /**
                         * Dimension configuration for [PrimaryField].
                         *
                         * @param shape Field shape.
                         */
                        data class PrimaryFieldDimens(
                            val shape: Shape
                        )

                        @Composable
                        fun dimens(
                            shape: Shape = RoundedCornerShape(10.dp)
                        ) = PrimaryFieldDimens(
                            shape = shape
                        )
                    }

                    /**
                     * Primary text input field with outlined style.
                     *
                     * ## Usage
                     *
                     * ```kotlin
                     * val textState = rememberTextFieldState()
                     *
                     * PrimaryField(
                     *     state = textState,
                     *     placeholder = "Enter your name",
                     *     modifier = Modifier.fillMaxWidth()
                     * )
                     * ```
                     *
                     * @param state Text field state
                     * @param modifier Applied to field
                     * @param focusRequester Optional focus requester
                     * @param placeholder Placeholder text when empty
                     * @param lineLimits Line limit configuration
                     * @param inputTransformation Input transformation for formatting
                     * @param leadingIcon Optional leading icon
                     * @param trailingIcon Optional trailing icon
                     * @param enabled Whether field is enabled
                     * @param readOnly Whether field is read-only
                     * @param keyboardOptions Keyboard configuration
                     * @param colors Color configuration, defaults to [PrimaryFieldDefaults.colors]
                     * @param style Text style configuration, defaults to [PrimaryFieldDefaults.style]
                     * @param dimens Dimension configuration, defaults to [PrimaryFieldDefaults.dimens]
                     */
                    @Composable
                    fun PrimaryField(
                        state: TextFieldState,
                        modifier: Modifier = Modifier.fillMaxWidth(),
                        focusRequester: FocusRequester? = null,
                        placeholder: String? = null,
                        lineLimits: TextFieldLineLimits = TextFieldLineLimits.SingleLine,
                        inputTransformation: InputTransformation? = null,
                        leadingIcon: (@Composable (() -> Unit))? = null,
                        trailingIcon: (@Composable (() -> Unit))? = null,
                        enabled: Boolean = true,
                        readOnly: Boolean = false,
                        keyboardOptions: KeyboardOptions = KeyboardOptions.Default,
                        colors: PrimaryFieldDefaults.PrimaryFieldColors = PrimaryFieldDefaults.colors(),
                        style: PrimaryFieldDefaults.PrimaryFieldStyle = PrimaryFieldDefaults.style(),
                        dimens: PrimaryFieldDefaults.PrimaryFieldDimens = PrimaryFieldDefaults.dimens()
                    ) {
                        OutlinedTextField(
                            state = state,
                            modifier = modifier.applyFocusRequester(focusRequester),
                            textStyle = style.text,
                            placeholder = placeholder?.let {
                                {
                                    Text(
                                        text = it,
                                        style = style.placeholder,
                                        color = colors.placeholder,
                                        modifier = Modifier.fillMaxWidth()
                                    )
                                }
                            },
                            lineLimits = lineLimits,
                            shape = dimens.shape,
                            inputTransformation = inputTransformation,
                            leadingIcon = leadingIcon,
                            trailingIcon = trailingIcon,
                            enabled = enabled,
                            readOnly = readOnly,
                            keyboardOptions = keyboardOptions,
                            colors = OutlinedTextFieldDefaults.colors(
                                focusedTextColor = colors.focusedText,
                                unfocusedTextColor = colors.unfocusedText,
                                focusedBorderColor = colors.focusedBorder,
                                unfocusedBorderColor = colors.unfocusedBorder,
                                cursorColor = colors.cursor,
                                selectionColors = colors.selection,
                                focusedPlaceholderColor = colors.placeholder,
                                unfocusedPlaceholderColor = colors.placeholder
                            )
                        )
                    }

                    private fun Modifier.applyFocusRequester(focusRequester: FocusRequester?): Modifier =
                        if (focusRequester != null) this.focusRequester(focusRequester) else this
                    """.trimIndent()
            )

            //https://github.com/RoyalTaxi/yalla-components.git
            KotlinCode(
                code = """
                    /**
                     * State for [IconButton] component.
                     *
                     * @property enabled When false, button is disabled.
                     * @property size Button size variant.
                     */
                    data class IconButtonState(
                        val enabled: Boolean = true,
                        val size: ButtonSize = ButtonSize.Medium
                    )

                    /**
                     * Icon-only button for compact actions.
                     *
                     * Use for actions represented by icons, like navigation, menu, or close buttons.
                     *
                     * ## Usage
                     *
                     * ```kotlin
                     * IconButton(
                     *     state = IconButtonState(),
                     *     onClick = onClose,
                     * ) {
                     *     Icon(Icons.Default.Close, contentDescription = "Close")
                     * }
                     * ```
                     *
                     * @param state Button state containing enabled and size.
                     * @param onClick Invoked on click.
                     * @param modifier Applied to button.
                     * @param colors Color configuration, defaults to [IconButtonDefaults.colors].
                     * @param dimens Dimension configuration, defaults to [IconButtonDefaults.dimens].
                     * @param content Icon content.
                     *
                     * @see IconButtonDefaults for default values
                     */
                    @Composable
                    fun IconButton(
                        state: IconButtonState,
                        onClick: () -> Unit,
                        modifier: Modifier = Modifier,
                        colors: IconButtonDefaults.IconButtonColors = IconButtonDefaults.colors(),
                        dimens: IconButtonDefaults.IconButtonDimens = IconButtonDefaults.dimens(),
                        content: @Composable () -> Unit,
                    ) {
                        Surface(
                            onClick = onClick,
                            modifier = modifier.size(dimens.size(state.size)),
                            enabled = state.enabled,
                            shape = dimens.shape,
                            color = colors.containerColor(state.enabled),
                            contentColor = colors.contentColor(state.enabled),
                        ) {
                            Box(contentAlignment = Alignment.Center) {
                                content()
                            }
                        }
                    }

                    /**
                     * Default configuration values for [IconButton].
                     *
                     * Provides theme-aware defaults for [colors] and [dimens] that can be overridden.
                     */
                    object IconButtonDefaults {

                        /**
                         * Color configuration for [IconButton].
                         *
                         * @param container Background color when enabled.
                         * @param content Icon color when enabled.
                         * @param disabledContainer Background color when disabled.
                         * @param disabledContent Icon color when disabled.
                         */
                        data class IconButtonColors(
                            val container: Color,
                            val content: Color,
                            val disabledContainer: Color,
                            val disabledContent: Color,
                        ) {
                            fun containerColor(enabled: Boolean): Color =
                                if (enabled) container else disabledContainer

                            fun contentColor(enabled: Boolean): Color =
                                if (enabled) content else disabledContent
                        }

                        @Composable
                        fun colors(
                            container: Color = System.color.backgroundTertiary,
                            content: Color = System.color.iconBase,
                            disabledContainer: Color = System.color.backgroundTertiary.copy(alpha = 0.5f),
                            disabledContent: Color = System.color.iconDisabled,
                        ) = IconButtonColors(
                            container = container,
                            content = content,
                            disabledContainer = disabledContainer,
                            disabledContent = disabledContent,
                        )

                        @Composable
                        fun filledColors(
                            container: Color = System.color.buttonActive,
                            content: Color = System.color.iconWhite,
                            disabledContainer: Color = System.color.buttonDisabled,
                            disabledContent: Color = System.color.iconWhite,
                        ) = IconButtonColors(
                            container = container,
                            content = content,
                            disabledContainer = disabledContainer,
                            disabledContent = disabledContent,
                        )

                        /**
                         * Dimension configuration for [IconButton].
                         *
                         * @param smallSize Button size for small variant.
                         * @param mediumSize Button size for medium variant.
                         * @param largeSize Button size for large variant.
                         * @param smallIconSize Icon size for small variant.
                         * @param mediumIconSize Icon size for medium variant.
                         * @param largeIconSize Icon size for large variant.
                         * @param cornerRadius Corner radius of the button shape.
                         * @param shape Button shape.
                         */
                        data class IconButtonDimens(
                            val smallSize: Dp,
                            val mediumSize: Dp,
                            val largeSize: Dp,
                            val smallIconSize: Dp,
                            val mediumIconSize: Dp,
                            val largeIconSize: Dp,
                            val cornerRadius: Dp,
                            val shape: Shape,
                        ) {
                            fun size(size: ButtonSize): Dp = when (size) {
                                ButtonSize.Small -> smallSize
                                ButtonSize.Medium -> mediumSize
                                ButtonSize.Large -> largeSize
                            }

                            fun iconSize(size: ButtonSize): Dp = when (size) {
                                ButtonSize.Small -> smallIconSize
                                ButtonSize.Medium -> mediumIconSize
                                ButtonSize.Large -> largeIconSize
                            }
                        }

                        @Composable
                        fun dimens(
                            smallSize: Dp = 36.dp,
                            mediumSize: Dp = 44.dp,
                            largeSize: Dp = 52.dp,
                            smallIconSize: Dp = 18.dp,
                            mediumIconSize: Dp = 22.dp,
                            largeIconSize: Dp = 26.dp,
                            cornerRadius: Dp = 12.dp,
                            shape: Shape = RoundedCornerShape(cornerRadius),
                        ) = IconButtonDimens(
                            smallSize = smallSize,
                            mediumSize = mediumSize,
                            largeSize = largeSize,
                            smallIconSize = smallIconSize,
                            mediumIconSize = mediumIconSize,
                            largeIconSize = largeIconSize,
                            cornerRadius = cornerRadius,
                            shape = shape,
                        )
                    }
                    """.trimIndent()
            )

            //CompositionLocal - Holder for shared data
            //.provides — Assigns a value to a CompositionLocal
            //.current - Reads the current value
            //CompositionLocalProvider — Makes a value available to child composables
            //Scope Only children of the provider can access it
            //Recomposition Only affects readers of the changed local

            WebImage(
                src = Resources.Images.COMPOSITION_LOCAL_DI_VS,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "CompositionLocal Vs DI"
            )

            SpanText(
                text = "CompositionLocal using context",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    val LocalTheme = compositionLocalOf { Theme() }
                    
                    context(Theme)
                    @Composable
                    fun MyComponent () {
                    }
                    """.trimIndent()
            )
        }
    }
}

//https://github.com/Syedwaheed/CineVault.git (LocalNavAnimatedContentScope, LocalAnimatedVisibilityScope, LocalSharedTransitionScope, BottomNavigation Width Scroll Manage)
//https://github.com/Chaintech-Network/ComposeMultiplatformMediaPlayer
//https://github.com/Grippo-app/grippo-mobile.git (Good for theme)
//https://github.com/L1ndenbaum/xiehe-multiplatform.git (Theme change, 3 Primary Color good)
//https://github.com/TinaT2/TimerzeerKMP.git (Theme change color, Font change, Background change)
//https://proandroiddev.com/composition-locals-in-jetpack-compose-a-beginner-to-advanced-guide-e6a812ca7620
//https://medium.com/@sehajkahlon437/compositionlocalprovider-the-secret-tunnel-system-your-compose-ui-didnt-tell-you-about-6bac8fb3b4f9
//https://github.com/imansprn/gost-desktop.git (Material3 good)
//https://blog.shreyaspatil.dev/exploring-compositionlocal-api-internals-in-jetpack-compose/?source=post_page-----f01155467e56---------------------------------------
//https://github.com/taetae98coding/Jarvis.git (Theme Change good)
//https://github.com/MeltingMadness/Thrym.git (LazyRow Animation, Chart, Liquid Glass Good, CompositionLocalProvider)